import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { formatRelative } from '../../../utils/relativeTime';

/**
 * Save status pill shown in the encounter banner.
 * Re-renders once per second so "Saved 12s ago" stays current.
 */
export default function SaveIndicator({ status, lastSavedAt, error, onRetry }) {
    const [, force] = useState(0);
    useEffect(() => {
        const t = setInterval(() => force((n) => n + 1), 5000);
        return () => clearInterval(t);
    }, []);

    let icon;
    let text;
    let color = 'text.secondary';

    switch (status) {
        case 'saving':
            icon = <CircularProgress size={12} thickness={6} sx={{ color: 'inherit' }} />;
            text = 'Saving…';
            color = 'text.secondary';
            break;
        case 'pending':
            icon = <EditNoteRoundedIcon sx={{ fontSize: 14 }} />;
            text = 'Editing…';
            color = 'text.secondary';
            break;
        case 'error':
            icon = <CloudOffRoundedIcon sx={{ fontSize: 14 }} />;
            text = 'Couldn’t save — click to retry';
            color = 'error.main';
            break;
        case 'saved':
            icon = <CheckRoundedIcon sx={{ fontSize: 14 }} />;
            text = `Saved ${formatRelative(lastSavedAt)}`;
            color = 'success.dark';
            break;
        default:
            icon = null;
            text = lastSavedAt ? `Saved ${formatRelative(lastSavedAt)}` : 'No changes yet';
            color = 'text.disabled';
    }

    const node = (
        <Box
            onClick={status === 'error' ? onRetry : undefined}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                bgcolor: status === 'error' ? 'rgba(211,47,47,0.08)' : 'rgba(5,47,93,0.04)',
                border: '1px solid',
                borderColor: status === 'error' ? 'rgba(211,47,47,0.25)' : 'rgba(5,47,93,0.10)',
                color,
                cursor: status === 'error' ? 'pointer' : 'default',
                transition: 'background-color 160ms ease, color 160ms ease',
                userSelect: 'none',
            }}
        >
            {icon}
            <Typography
                variant="caption"
                sx={{ fontWeight: 500, letterSpacing: 0.1, lineHeight: 1 }}
            >
                {text}
            </Typography>
        </Box>
    );

    if (error && status === 'error') {
        return <Tooltip title={String(error.message || error)} arrow>{node}</Tooltip>;
    }
    return node;
}
